import "./treeView.scss";

import { List, Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { DragDropTypes } from "../../constants/dragDropTypes";
import { TREE_VIEW } from "../../constants/identifiers";
import keyConstants from "../../constants/keyConstants";
import tableDropAction from "../../constants/tableDropAction";
import { batchActions } from "../../modules/chainReducer";
import { PropTypeShapes } from "../../types";
import { isChildInParentView } from "../../utils/domUtils";
import { asDropTarget } from "../dragAndDrop/DroppableComponent";
import TreeItemView from "./TreeItemView";

const canDropOnComponent = (props, dragItem) => {
  return props.onCanDrop && dragItem.itemType != DragDropTypes.TABLE_COLUMN
    ? props.onCanDrop(dragItem.multipleItemIds)
    : false;
};

const dropCallbacks = {
  onEnter: (props, dragItem, dropTarget) => {
    return canDropOnComponent(props, dragItem) ? dropTarget.component.onEnter(props, dragItem, dropTarget) : false;
  },

  onLeave: (props, _dragItem, dropTarget) => {
    return dropTarget.component.onLeave();
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    const multipleIds =
      props.itemIdsOfType && props.selectedItemIds.contains(dragItem.itemId)
        ? props.itemIdsOfType.filter(id => props.selectedItemIds.contains(id)).toArray()
        : [dragItem.itemId];
    return multipleIds;
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onHover(props, dragItem, positions, dropTarget)
      : false;
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    return canDropOnComponent(props, dragItem)
      ? dropTarget.component.onDrop(props, dragItem, positions, dropTarget)
      : false;
  }
};

const findDropIndex = (sortedDimensions, dragCenter, rootItems) => {
  let dropIndex = _.findIndex(sortedDimensions, d => dragCenter > d.top && dragCenter < d.bottom);
  let dropAction = tableDropAction.DropOnRow;
  let isDropIndexLastRoot = false;
  if (dropIndex === -1) {
    if (dragCenter >= _.last(sortedDimensions).bottom) {
      const lastRootItem = rootItems.last();
      dropIndex = _.findIndex(sortedDimensions, d => d.itemId === lastRootItem.id);
      dropAction = tableDropAction.DropBelowRow;
      isDropIndexLastRoot = true;
    } else if (dragCenter < _.first(sortedDimensions).top) {
      dropIndex = 0;
      dropAction = tableDropAction.DropAboveRow;
    }
  }
  return { dropIndex, dropAction, isDropIndexLastRoot };
};

const findFirstIndexBelowCenter = (sortedDimensions, dragCenter) => {
  let targetIndex = _.findIndex(sortedDimensions, d => d.top > dragCenter);
  let targetAction = tableDropAction.DropAboveRow;
  if (targetIndex === -1 && sortedDimensions.length) {
    targetIndex = sortedDimensions.length - 1;
    targetAction = tableDropAction.DropBelowRow;
  }
  return { targetIndex, targetAction };
};

const renderItemAndChildren = (
  item,
  indentLevel,
  expandedItems,
  selectedItemIds,
  onToggleItem,
  onItemClick,
  onItemDoubleClick,
  onLeftIconClick,
  onItemStatusClick,
  onReportDimensions,
  onLabelChange,
  onCanDrag,
  viewId,
  width,
  droppingOnItemId,
  droppingAboveItemId,
  droppingBelowItemId,
  noDropIndent,
  isDroppingGroup,
  autoEditBlankNodes,
  refMap,
  disabledItemIds,
  disableInlineEditing,
  isEditMode,
  pulsateIconId
) => {
  let els = [];
  const isDroppingOn = droppingOnItemId === item.id;
  const isDroppingAbove = droppingAboveItemId === item.id;
  const isDroppingBelow = droppingBelowItemId === item.id;
  const isExpanded = item.children && item.children.size > 0 && expandedItems.contains(item.id);
  // Add item for the current tree
  els.push(
    <div key={item.id} ref={refMap.get(item.id)}>
      <TreeItemView
        indentLevel={indentLevel}
        id={item.id}
        index={item.index}
        type={item.type}
        label={item.label}
        icon={item.icon}
        statusIcon={item.statusIcon}
        labelColor={item.labelColor}
        iconColor={item.iconColor}
        statusColor={item.statusColor}
        statusLabel={item.statusLabel}
        statusIconIdentifier={item.statusIconIdentifier}
        itemChildren={item.children}
        isExpanded={isExpanded}
        isSelected={selectedItemIds.contains(item.id)}
        onToggle={onToggleItem}
        onClick={onItemClick}
        onDoubleClick={onItemDoubleClick}
        onLeftIconClick={onLeftIconClick}
        onStatusClick={onItemStatusClick}
        onReportDimensions={onReportDimensions}
        onLabelChange={label => onLabelChange([item.id], label)}
        canDrag={onCanDrag ? onCanDrag(item.id) : false}
        viewId={viewId}
        width={width}
        isDroppingOn={isDroppingOn}
        isDroppingAbove={isDroppingAbove}
        isDroppingBelow={isDroppingBelow}
        noDropIndent={noDropIndent}
        isDroppingGroup={isDroppingGroup}
        autoEditWhenBlank={autoEditBlankNodes}
        disabled={disableInlineEditing}
        isEditMode={isEditMode}
        pulsateIconId={pulsateIconId}
        placeholder={item.placeholder ? item.placeholder : "Untitled"}
      />
    </div>
  );

  // And if it is expanded, draw any children
  if (isExpanded) {
    item.children.forEach(i => {
      els.push(
        ...renderItemAndChildren(
          i,
          indentLevel + 1,
          expandedItems,
          selectedItemIds,
          onToggleItem,
          onItemClick,
          onItemDoubleClick,
          onLeftIconClick,
          onItemStatusClick,
          onReportDimensions,
          onLabelChange,
          onCanDrag,
          viewId,
          width,
          droppingOnItemId,
          droppingAboveItemId,
          droppingBelowItemId,
          noDropIndent,
          isDroppingOn,
          autoEditBlankNodes,
          refMap,
          disabledItemIds,
          disableInlineEditing,
          isEditMode,
          pulsateIconId
        )
      );
    });
  }
  return els;
};

const renderItems = (
  items,
  expandedItems,
  selectedItemIds,
  onToggleItem,
  onItemClick,
  onItemDoubleClick,
  onLeftIconClick,
  onItemStatusClick,
  onReportDimensions,
  onLabelChange,
  onCanDrag,
  viewId,
  width,
  droppingOnItemId,
  droppingAboveItemId,
  droppingBelowItemId,
  noDropIndent,
  autoEditBlankNodes,
  refMap,
  disabledItemIds,
  disableInlineEditing,
  isEditMode,
  pulsateIconId
) => {
  let els = [];
  items.forEach(i =>
    els.push(
      renderItemAndChildren(
        i,
        0,
        expandedItems,
        selectedItemIds,
        onToggleItem,
        onItemClick,
        onItemDoubleClick,
        onLeftIconClick,
        onItemStatusClick,
        onReportDimensions,
        onLabelChange,
        onCanDrag,
        viewId,
        width,
        droppingOnItemId,
        droppingAboveItemId,
        droppingBelowItemId,
        noDropIndent,
        null,
        autoEditBlankNodes,
        refMap,
        disabledItemIds,
        disableInlineEditing,
        isEditMode,
        pulsateIconId
      )
    )
  );
  return els;
};

const createRefs = items =>
  items.reduce((refMap, item) => {
    return refMap.set(item.id, React.createRef());
  }, Map());

class TreeView extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      droppingOnItemId: null,
      droppingAboveItemId: null,
      droppingBelowItemId: null
    };

    this.onReportDimensions = this.onReportDimensions.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);

    this.onEnter = this.onEnter.bind(this);
    this.onHover = this.onHover.bind(this);

    this._itemDimensions = Map();

    this._lastReducerAction = null;

    this._renderingItems = [];

    this.refMap = Map();

    this._treeViewScrollTop = 0;
    this._scrollTopHaveBeenChanged = false;
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.targetItemType !== nextProps.targetItemType) {
      this._itemDimensions = Map();
    }
    if (this.props.rootItems !== nextProps.rootItems) {
      this.refMap = createRefs(nextProps.rootItems);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.rootItems.size === prevProps.rootItems.size + 1) {
      const prevItems = prevProps.rootItems;
      const thisItems = this.props.rootItems;
      const newItemId = thisItems.find(item => prevItems.every(pi => pi.id !== item.id)).id;
      const newItemRef = this.refMap.get(newItemId).current;
      if (newItemRef) {
        newItemRef.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }
    if (this._scrollTopHaveBeenChanged) {
      this.updateItemDimensionsForDragging();
      this._scrollTopHaveBeenChanged = false;
    }
  }

  clearDragAndDropStates() {
    this.setState({ droppingOnItemId: null, droppingAboveItemId: null, droppingBelowItemId: null });
  }

  onLeave() {
    this._lastDropAction = null;
    this._lastDropIndex = null;
    this._lastReducerAction = null;
    this.clearDragAndDropStates();
    if (this.props.onDragLeave) {
      this.props.onDragLeave();
    }
  }

  getDragStartParams(_props, _dragItem, positions, _dropTarget) {
    const screenPointerPos = _.clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    return {
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
  }

  updateItemDimensionsForDragging() {
    this._itemDimensionsExcludingDescendantsOfDrag = this._itemDimensions
      .filter(d => !this._draggedDescendants.has(d.itemId))
      .toJS();
    this._sortedItemDimensionsExcludingDescendantsOfDrag = _.sortBy(
      this._itemDimensionsExcludingDescendantsOfDrag,
      d => d.top
    );

    // Get all item dimensions sorted
    this._allSortedItemDimensions = _.sortBy(this._itemDimensions.toJS(), d => d.top);
  }

  onEnter(_props, dragItem, _dropTarget) {
    const itemsById = this.props.itemsById;
    // Calculate these values once and cache them to avoid recalculating the values every time
    this._dragItemTypes = _.map(dragItem.multipleItemIds, id => itemsById.get(id).type);
    // We cannot drop of any dragged items
    this._draggedDescendants = this.props.getDescendantsOfItems(dragItem.multipleItemIds);
    // .union(dragItem.multipleItemIds);

    this.updateItemDimensionsForDragging();
  }

  onHover(props, dragItem, positions, _dropTarget) {
    const { itemsById } = this.props;

    // Should update the dimensions map when this tree view scrolls
    if (this._domNode && this._domNode.scrollTop !== this._treeViewScrollTop) {
      this._scrollTopHaveBeenChanged = true;
    }

    this._treeViewScrollTop = this._domNode.scrollTop;

    // Grab a copy of dimensions, but filter out the dragged item and all of their children,
    // since we don't want to respond to hovering over those items
    const sortedDimensions = this._sortedItemDimensionsExcludingDescendantsOfDrag;

    // First, we need to work out which item we are hovering over, as well as its super
    const dragCenter = (positions.sourceRect.top + positions.sourceRect.bottom) / 2;

    // Do not accept drop if there is literally nothing in the view!
    if (sortedDimensions.length === 0) {
      return;
    }

    let { dropIndex, dropAction, isDropIndexLastRoot } = findDropIndex(
      sortedDimensions,
      dragCenter,
      this.props.rootItems
    );

    if (_.isNil(this._lastDropIndex)) {
      this._lastDropIndex = dropIndex;
    }

    // dropIndex can only be -1 if we are hovering over one of the dragged items, and we don't want to
    // respond to that. So just totally ignore it dude.
    if (dropIndex < 0) {
      return;
    }

    // Which item are we dropping on?
    let dropDimension = sortedDimensions[dropIndex];
    let dropItemId = dropDimension.itemId;

    if (dragItem.multipleItemIds.indexOf(dropItemId) >= 0) {
      return;
    }

    let dCenter = (dropDimension.top + dropDimension.bottom) / 2;
    let dropItem = itemsById.get(dropItemId);
    let dropType = dropItem.type;
    let canDropOnItem =
      dropAction === tableDropAction.DropOnRow &&
      _.every(this._dragItemTypes, t => this.props.canTypeHaveSuperType(t, dropType));
    let canDropAdjacent = Boolean(_.find(this._dragItemTypes, t => t === dropType)) || isDropIndexLastRoot;

    // If we can neither drop on the item or next to it,
    // let's see if we can find something we *can* drop on first
    if (!canDropOnItem && !canDropAdjacent) {
      const initialDropIndex = dropIndex;
      dropIndex = initialDropIndex - 1;
      // First, try everything above us to see if we can go anywhere there.
      while (dropIndex >= 0 && !canDropAdjacent) {
        dropDimension = sortedDimensions[dropIndex];
        dCenter = (dropDimension.top + dropDimension.bottom) / 2;
        dropItemId = dropDimension.itemId;
        dropItem = itemsById.get(dropItemId);
        dropType = dropItem.type;
        canDropAdjacent = Boolean(_.find(this._dragItemTypes, t => t === dropType));
        if (!canDropAdjacent) {
          dropIndex--;
        }
      }

      // And if that fails, try going down instead
      if (!canDropOnItem && !canDropAdjacent) {
        const dimensionsLength = sortedDimensions.length;
        dropIndex = initialDropIndex + 1;
        while (dropIndex < dimensionsLength && !canDropAdjacent) {
          dropDimension = sortedDimensions[dropIndex];
          dCenter = (dropDimension.top + dropDimension.bottom) / 2;
          dropItemId = dropDimension.itemId;
          dropItem = itemsById.get(dropItemId);
          dropType = dropItem.type;
          canDropAdjacent = Boolean(_.find(this._dragItemTypes, t => t === dropType));
          if (!canDropAdjacent) {
            dropIndex++;
          }
        }
      }
    }

    const isDropTargetExpanded = this.props.expandedItems.contains(dropItemId);

    // dropAction will be reported as DropOnRow by findDropIndex if the drop position overlaps with
    // the item... otherwise, it will report drop above or drop below.
    // But we still need to clean this up and check it is valid for these specific items, and whether it sits in the
    // upper, middle or lower portion of the target... as the earlier test only looks at overlap in y positions
    if (dropAction === tableDropAction.DropOnRow) {
      const aboveLimit = canDropOnItem ? dCenter - 10 : dCenter;
      const belowLimit = canDropOnItem ? dCenter + 10 : dCenter;

      if (canDropAdjacent && dragCenter < aboveLimit) {
        dropAction = tableDropAction.DropAboveRow;
      } else if (canDropAdjacent && dragCenter >= belowLimit && (!canDropOnItem || !isDropTargetExpanded)) {
        dropAction = tableDropAction.DropBelowRow;
      } else if (!canDropOnItem) {
        dropAction = null;
      }
    } else if (!canDropAdjacent) {
      dropAction = null;
    }

    if (!dropAction || dropIndex < 0 || (dropIndex === this._lastDropIndex && dropAction === this._lastDropAction)) {
      return;
    }

    // Grab the super, and make sure we can drop on that as well.
    const dropSuperId = dropAction === tableDropAction.DropOnRow ? dropItemId : dropItem.super;
    const dropSuper = dropSuperId ? itemsById.get(dropSuperId) : null;

    const canDropOnParent = dropSuperId
      ? _.every(this._dragItemTypes, t => this.props.canTypeHaveSuperType(t, dropSuper.type))
      : true;

    // If we can't drop on this target, and they have not dragged far enough to move past it, then just ignore it for now
    // Since we haven't set lastDropIndex yet, we will reach this point again until they have moved far enough.
    if (!dropAction || !canDropOnParent) {
      // Note: this return is deliberately placed above this._lastDropIndex being set below
      return;
    }

    // Is drop parent expanded? Always YES if no drop parent (i.e. root)
    const isDropSuperExpanded = !dropSuperId || this.props.expandedItems.contains(dropSuperId);

    let reducerActions = [];
    let idsNotInParent = [];

    dragItem.multipleItemIds.forEach(dragId => {
      // First, make sure this item is in the correct parent
      const draggedItem = itemsById.get(dragId);
      // If it is not inside the target parent, move it there
      if (draggedItem.super !== dropSuperId) {
        idsNotInParent.push(dragId);
      }

      // If the parent is expanded (or it is root), then we also need to find the correct
      // drop position for the item
      if (isDropSuperExpanded && dropAction !== tableDropAction.DropOnRow) {
        const itemType = draggedItem.type;
        // If the items are the same type, it is easy, just slot it in near that item
        if (itemType === dropType) {
          const indexAction = this.getDropIndexAction(draggedItem, dropItem, dropAction);
          if (indexAction) {
            reducerActions.push(indexAction);
          }
        } else {
          // If they are different types, then just find the first item BELOW our dragCenter and
          // place it above that...

          // First, we need to filter down the drop targets to only the items matching this type
          // and within the target parent
          const dimensionsOfType = _.filter(sortedDimensions, dim => {
            const targetItem = itemsById.get(dim.itemId);
            return targetItem.type === itemType && targetItem.super === dropSuperId;
          });

          // Now find the target within that list
          const { targetIndex, targetAction } = findFirstIndexBelowCenter(dimensionsOfType, dragCenter);
          if (targetIndex >= 0) {
            const targetId = dimensionsOfType[targetIndex].itemId;
            const targetItem = itemsById.get(targetId);
            const indexAction = this.getDropIndexAction(draggedItem, targetItem, targetAction);
            if (indexAction) {
              reducerActions.push(indexAction);
            }
          }
        }
      }
    });

    if (idsNotInParent.length) {
      reducerActions.unshift(this.props.getSuperAction(idsNotInParent, dropSuperId));
    }

    if (reducerActions.length) {
      const finalReducerAction = reducerActions.length > 1 ? batchActions(reducerActions) : reducerActions[0];
      this._lastReducerAction = finalReducerAction;
    } else {
      this._lastReducerAction = null;
    }

    const droppingOnItemId = dropAction === tableDropAction.DropOnRow ? dropItemId : null;
    let droppingAboveItemId = dropAction === tableDropAction.DropAboveRow ? dropItemId : null;
    let droppingBelowItemId = dropAction === tableDropAction.DropBelowRow ? dropItemId : null;

    const isDropBelowExpanded = Boolean(droppingBelowItemId) && this.props.expandedItems.contains(droppingBelowItemId);

    // If the item we are dropping "below" is expanded, we actually have to move the drop indicator
    // to "above" the next item that is not a descendant of the dropItemId
    // On the off chance there is no further items, we will draw it under the last child... but this *will* display with
    // an incorrect indentation, so it is a last resort.
    if (isDropBelowExpanded) {
      const descendants = this.props.getDescendantsOfItems([droppingBelowItemId]);
      // go down until we find the last item below this item that is a descendant
      const belowIndex = _.findIndex(this._allSortedItemDimensions, d => d.itemId === dropItemId);
      const aboveIndex = _.findIndex(
        this._allSortedItemDimensions,
        (d, i) => i >= belowIndex && d.itemId !== droppingBelowItemId && !descendants.contains(d.itemId)
      );

      if (aboveIndex >= 0) {
        droppingAboveItemId = aboveIndex >= 0 ? this._allSortedItemDimensions[aboveIndex].itemId : null;
        droppingBelowItemId = null;
      } else {
        droppingBelowItemId = _.last(this._allSortedItemDimensions).itemId;
        isDropIndexLastRoot = true;
      }
    }

    let newState = {};
    let stateUpdateRequired = false;

    if (this.state.droppingOnItemId !== droppingOnItemId) {
      newState.droppingOnItemId = droppingOnItemId;
      stateUpdateRequired = true;
    }
    if (this.state.droppingAboveItemId !== droppingAboveItemId) {
      newState.droppingAboveItemId = droppingAboveItemId;
      stateUpdateRequired = true;
    }
    if (this.state.droppingBelowItemId !== droppingBelowItemId) {
      newState.droppingBelowItemId = droppingBelowItemId;
      stateUpdateRequired = true;
    }
    if (this.state.noDropIndent !== isDropIndexLastRoot) {
      newState.noDropIndent = isDropIndexLastRoot;
      stateUpdateRequired = true;
    }

    if (stateUpdateRequired) {
      this.setState(newState);
    }

    this._lastDropIndex = dropIndex;
    this._lastDropAction = dropAction;
    this._lastDragCenter = dragCenter;
  }

  onDrop(_props, _dragItem, _positions, _dropTarget) {
    if (this._lastReducerAction) {
      this.props.onDispatchDropAction(this._lastReducerAction);
    }
    this._lastReducerAction = null;
    this._lastDropIndex = null;
    this._lastDropAction = null;
    this.clearDragAndDropStates();
  }

  getDropIndexAction(draggedItem, dropItem, dropActionType) {
    const dragItemType = draggedItem.type;
    const dropItemType = dropItem.type;
    if (dragItemType === dropItemType) {
      const itemIdsOfThisType = this.props.itemIdsByType.get(dragItemType);
      const dropItemIndex = itemIdsOfThisType.findIndex(id => id === dropItem.id);
      const dragItemIndex = itemIdsOfThisType.findIndex(id => id === draggedItem.id);
      // Change the index of the drag item
      // to the closest index before/after the drop item
      const changeIndex =
        dropActionType === tableDropAction.DropAboveRow
          ? dragItemIndex > dropItemIndex
            ? dropItemIndex
            : dropItemIndex - 1
          : dragItemIndex < dropItemIndex
            ? dropItemIndex
            : dropItemIndex + 1;
      if (dropItemIndex >= 0) {
        // Set the drag item being the index before/after the drop item
        // and make it under the same super
        return this.props.getIndexAction(draggedItem.type, draggedItem.id, changeIndex);
      }
    }
    return null;
  }

  onReportDimensions(itemId, rect) {
    this._itemDimensions = rect
      ? this._itemDimensions.set(itemId, {
        itemId: itemId,
        top: rect.top,
        bottom: rect.bottom
      })
      : this._itemDimensions.delete(itemId);
  }

  scrollToItem(itemId) {
    const itemRef = this.refMap.get(itemId);
    const { _domNode } = this;
    if (itemRef && itemRef.current && _domNode && !isChildInParentView(_domNode, itemRef.current)) {
      itemRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }

  onKeyDown(e) {
    if (e.key === keyConstants.Down || e.key === keyConstants.Up) {
      e.stopPropagation();
      e.preventDefault();
      const { selectedItemIds, onItemClick } = this.props;
      let nextItemIndex = this._renderingItems.findIndex(id => selectedItemIds.includes(id));
      const itemCount = this._renderingItems.length;
      if (e.key === keyConstants.Down && nextItemIndex < itemCount - 1) {
        nextItemIndex += 1;
      } else if (e.key === keyConstants.Up && nextItemIndex > 0) {
        nextItemIndex -= 1;
      }
      const newValue = this._renderingItems[nextItemIndex];
      this.scrollToItem(newValue);
      onItemClick(newValue, {});
    }
  }

  renderItems() {
    const items = renderItems(
      this.props.rootItems,
      this.props.expandedItems,
      this.props.selectedItemIds,
      this.props.onToggleItem,
      this.props.onItemClick,
      this.props.onItemDoubleClick,
      this.props.onLeftIconClick,
      this.props.onItemStatusClick,
      this.onReportDimensions,
      this.props.onLabelChange,
      this.props.onCanDrag,
      this.props.viewId,
      this.props.width,
      this.state.droppingOnItemId,
      this.state.droppingAboveItemId,
      this.state.droppingBelowItemId,
      this.state.noDropIndent,
      this.props.autoEditBlankNodes,
      this.refMap,
      this.props.disabledItemIds,
      this.props.disableInlineEditing,
      this.props.isEditMode,
      this.props.pulsateIconId
    );
    this._renderingItems = _.flattenDeep(items).map(i => i.key);
    return items;
  }

  onClick(_e) {
    if (this._domNode) {
      this._domNode.focus();
    }
  }

  render() {
    const classes = ["treeView"];

    const isDragging = this.state.droppingOnItemId || this.state.droppingAboveItemId || this.state.droppingBelowItemId;
    if (isDragging) {
      classes.push("isDragging");
    }

    return (
      <div
        className={classes.join(" ")}
        ref={node => (this._domNode = node)}
        onKeyDown={this.onKeyDown}
        tabIndex={0}
        onClick={this.onClick}
      >
        {this.renderItems()}
      </div>
    );
  }
}

TreeView.defaultProps = {
  expandedItems: List()
};

TreeView.propTypes = {
  rootItems: PropTypeShapes.treeViewItemList,
  expandedItems: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  onToggleItem: PropTypes.func,
  onLeftIconClick: PropTypes.func,
  onItemClick: PropTypes.func,
  onItemDoubleClick: PropTypes.func,
  onItemStatusClick: PropTypes.func,
  onLabelChange: PropTypes.func,
  targetItemType: PropTypes.string,
  width: PropTypes.number.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  disabledItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  disableInlineEditing: PropTypes.bool,
  isEditMode: PropTypes.bool,

  // Drag and drop
  itemsById: ImmutablePropTypes.map,
  onDragLeave: PropTypes.func,
  itemIdsOfType: ImmutablePropTypes.listOf(PropTypes.string),
  itemIdsByType: ImmutablePropTypes.map,
  onDispatchDropAction: PropTypes.func,
  onCanDrop: PropTypes.func,
  onCanDrag: PropTypes.func,
  viewId: PropTypes.string.isRequired,
  droppingItemId: PropTypes.string,
  getSuperAction: PropTypes.func,
  getIndexAction: PropTypes.func,
  superActionType: PropTypes.string,
  indexActionType: PropTypes.string,
  canTypeHaveSuperType: PropTypes.func,
  getDescendantsOfItems: PropTypes.func,

  pulsateIconId: PropTypes.string,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired
};

export default asDropTarget(TreeView, TREE_VIEW);
